/**
 * Shared utilities for material length location selection (Move modal, Modify Lengths modal).
 * Used to compute candidate locations, availability labels, and fit checks.
 */
import React from 'react';
import { Tag } from 'antd';

/**
 * Option shape for the per-row location <Select>. `isCurrent` flags the bar's
 * existing location (backend `is_current_location`) so the UI can pre-select it
 * and render a colored tag indicating the bar can stay where it is.
 */
export type LocationOption = {
	value: string;
	label: string;
	disabled?: boolean;
	isCurrent?: boolean;
	/** What typing matches: the short label and the saved one (see locationSearchText). */
	searchText?: string;
};

/**
 * `filterOption` for location Selects: matches `searchText` when set, so both
 * "T1-A2" and "T1 - Shelf 2 - A2" find the option, else the label.
 */
export function filterLocationOption(input: string, option?: Partial<LocationOption>): boolean {
	const haystack = option?.searchText ?? option?.label ?? '';
	return String(haystack).toLowerCase().includes(input.trim().toLowerCase());
}

/** Label shown in the colored tag next to a bar's current location. */
export const CURRENT_LOCATION_TAG_LABEL = 'Current';

/**
 * Render an option's content for AntD `<Select optionRender>`: plain label for
 * normal locations, label + colored "Current" tag for the bar's current one.
 * Written with React.createElement so this stays a .ts module.
 */
export function renderLocationOption(option?: LocationOption): React.ReactNode {
	if (!option) return null;
	if (!option.isCurrent) return option.label;
	return React.createElement(
		'span',
		{ style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
		option.label,
		React.createElement(
			Tag,
			{
				color: 'green',
				style: {
					marginInlineEnd: 0,
					fontSize: 10,
					lineHeight: '16px',
					padding: '0 6px',
				},
			},
			CURRENT_LOCATION_TAG_LABEL
		)
	);
}

/**
 * Sentinel option for a material length that has been fully consumed (New Length = 0).
 * A consumed bar holds no physical location, so the Location dropdown offers this option
 * instead of candidate locations. On submit it maps to a null inventory_location.
 */
export const NO_LOCATION_VALUE = '__NO_LOCATION__';
export const NO_LOCATION_LABEL = 'No Location Assigned';

/** Parse API decimal (string or number) to number; null/undefined -> 0 */
export function toNum(v: string | number | null | undefined): number {
	if (v == null) return 0;
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : 0;
}

/**
 * Effective remaining availability of a location for the current session.
 * For INDIVIDUAL_SLOTS the limiting metric is `bars` (2D packing capacity minus
 * bars already stored). Width/height remain only as informational fields.
 */
export type EffectiveAvailable = {
	weight: number;
	volume: number;
	width: number;
	height: number;
	bars?: number;
};

/** Format effective availability for the "Available" column (no parentheses) */
export function getAvailableColumnLabel(
	type: string,
	effectiveAvailable: EffectiveAvailable
): string {
	const t = type ?? '';
	if (t === 'SHARED_SHELVES') return `${effectiveAvailable.weight.toFixed(0)} lbs left`;
	if (t === 'SHARED_BINS') return `${effectiveAvailable.volume.toFixed(0)} in³ left`;
	if (t === 'INDIVIDUAL_SLOTS') {
		const bars = effectiveAvailable.bars ?? 0;
		return `${bars} bar${bars === 1 ? '' : 's'} left`;
	}
	if (t === 'OVERFLOW' || t === 'SHOP_FLOOR') return 'No limit';
	return '—';
}

/** True when the location has no effective availability for its limiting dimension (should be disabled in dropdown) */
export function hasNoAvailability(
	type: string,
	effectiveAvailable: EffectiveAvailable
): boolean {
	const t = type ?? '';
	if (t === 'SHARED_SHELVES') return effectiveAvailable.weight <= 0;
	if (t === 'SHARED_BINS') return effectiveAvailable.volume <= 0;
	if (t === 'INDIVIDUAL_SLOTS') return (effectiveAvailable.bars ?? 0) <= 0;
	return false; // OVERFLOW, SHOP_FLOOR, etc. have no limit
}

/** True if this bar's consumption fits in the location's effective remaining capacity (backend rules by type) */
export function barFitsInLocation(
	loc: {
		type?: string;
		available_volume?: string | number | null;
		available_weight?: string | number | null;
		available_width?: string | number | null;
		available_height?: string | number | null;
		available_bars?: string | number | null;
	},
	consumption: {
		weight_per_bar: string | number;
		volume_per_bar: string | number;
		width_per_bar: string | number;
		height_per_bar: string | number;
	},
	effectiveAvailable: EffectiveAvailable
): boolean {
	const type = loc.type ?? '';
	const w = toNum(consumption.weight_per_bar);
	const v = toNum(consumption.volume_per_bar);
	if (type === 'SHARED_SHELVES') {
		const limit = effectiveAvailable.weight;
		if (limit === 0 && loc.available_weight == null) return true; // no limit
		return limit >= w;
	}
	if (type === 'SHARED_BINS') {
		const limit = effectiveAvailable.volume;
		if (limit === 0 && loc.available_volume == null) return true;
		return limit >= v;
	}
	if (type === 'INDIVIDUAL_SLOTS') {
		// Each bar consumes exactly one slot of 2D capacity.
		const limitBars = effectiveAvailable.bars ?? 0;
		if (limitBars === 0 && loc.available_bars == null) return true; // unknown / no limit
		return limitBars >= 1;
	}
	// OVERFLOW, SHOP_FLOOR, etc. — no numeric limit
	return true;
}

export function getDimensionValue(record: any, column: string): number | null {
	const rawShape = record.material_shape?.display_name || record.material_shape;
	const shapeName = rawShape === 'Extrusion' ? 'Rectangle Bar' : rawShape;
	if (column === 'height_od') {
		return [
			'Plate',
			'Channel',
			'Angle',
			'Rectangle Bar',
			'Rectangle Tube',
			'Hex Bar',
		].includes(shapeName)
			? record.height
			: ['Round Bar', 'Round Tube'].includes(shapeName)
				? record.outer_diameter
				: null;
	}
	if (column === 'width_id') {
		return [
			'Plate',
			'Channel',
			'Angle',
			'Rectangle Bar',
			'Rectangle Tube',
		].includes(shapeName)
			? record.width
			: shapeName === 'Round Tube'
				? record.inner_diameter
				: null;
	}
	return null;
}

/** Shape names that use height (rectangular/plate/hex). */
const HEIGHT_SHAPES = ['Plate', 'Channel', 'Angle', 'Rectangle Bar', 'Rectangle Tube', 'Hex Bar', 'Extrusion'];
/** Shape names that use width (rectangular/plate, not hex). */
const WIDTH_SHAPES = ['Plate', 'Channel', 'Angle', 'Rectangle Bar', 'Rectangle Tube', 'Extrusion'];
/** Shape names that use outer diameter. */
const OD_SHAPES = ['Round Bar', 'Round Tube'];
/** Shape names that use inner diameter. */
const ID_SHAPES = ['Round Tube'];
/** Shape names that use wall thickness. */
const WALL_SHAPES = ['Rectangle Tube', 'Angle', 'Channel', 'Round Tube'];

/**
 * Returns the display value for a dimension column in a grid that shows all dimensions
 * (Height, Width, OD, ID, Wall). For the given record and dimension, returns the
 * numeric value when the record's shape uses that dimension, otherwise null.
 */
export function getDimensionCellValue(
	record: any,
	dimension: 'height' | 'width' | 'od' | 'id' | 'wall'
): number | null {
	const shapeName =
		record.material_shape?.display_name || record.material_shape;
	switch (dimension) {
		case 'height':
			return HEIGHT_SHAPES.includes(shapeName) ? (record.height != null ? Number(record.height) : null) : null;
		case 'width':
			return WIDTH_SHAPES.includes(shapeName) ? (record.width != null ? Number(record.width) : null) : null;
		case 'od':
			return OD_SHAPES.includes(shapeName) ? (record.outer_diameter != null ? Number(record.outer_diameter) : null) : null;
		case 'id':
			return ID_SHAPES.includes(shapeName) ? (record.inner_diameter != null ? Number(record.inner_diameter) : null) : null;
		case 'wall':
			return WALL_SHAPES.includes(shapeName) ? (record.wall_thickness != null ? Number(record.wall_thickness) : null) : null;
		default:
			return null;
	}
}

export function getMaterialInfoFromRecord(record: any) {
	const rawShape = record.material_shape?.display_name || record.material_shape;
	const shapeName = rawShape === 'Extrusion' ? 'Rectangle Bar' : rawShape;
	const heightVal = getDimensionValue(record, 'height_od');
	const widthVal = getDimensionValue(record, 'width_id');
	return {
		type: record.material_type?.display_name ?? 'N/A',
		width: widthVal != null ? widthVal : undefined,
		height: heightVal != null ? heightVal : undefined,
		outer_diameter: shapeName && ['Round Bar', 'Round Tube'].includes(shapeName) ? (Number(record.outer_diameter) || undefined) : undefined,
		inner_diameter: shapeName === 'Round Tube' ? (Number(record.inner_diameter) || undefined) : undefined,
		wall_thickness: record.wall_thickness != null ? Number(record.wall_thickness) : undefined,
		shape: rawShape ?? undefined,
	};
}
